import { router, usePage } from "@inertiajs/react"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

import { HelpSheet } from "@/components/help/help-sheet"
import type { SharedData } from "@/types"

export interface HelpArticleSummary {
  slug: string
  title: string
  summary: string
  section: string
}

interface HelpContextValue {
  /** Open the sheet on an article. Never navigates. */
  open: (slug: string) => void
  close: () => void
  /** The article that explains the current screen, if there is one. */
  pageSlug: string | null
  /** Titles and summaries for every desk article — what ⌘K searches. */
  index: HelpArticleSummary[]
  openSlug: string | null
}

// Exported so `HelpLearnMore` can read it optionally — it renders inside
// components that may sit outside the provider, and absent beats a crash.
export const HelpContext = createContext<HelpContextValue | null>(null)

/**
 * The context when there is one, null when there is not.
 *
 * The assistant panel renders in the same tree, but its markdown component is
 * also used in places that do not mount the provider. A help link that quietly
 * stays a link beats a crash inside a streaming reply.
 */
export function useHelpOptional() {
  return useContext(HelpContext)
}

export function useHelp() {
  const context = useContext(HelpContext)
  if (!context) throw new Error("useHelp must be used inside HelpProvider")

  return context
}

/**
 * Contextual help (INCO-386).
 *
 * A broker mid-trade who clicks help and loses their screen does not click it
 * twice. The sheet slides in over the page, and the page underneath is
 * untouched — no navigation, no scroll position lost, no form abandoned.
 *
 * Mounted once in `PersistentLayout`, next to the assistant panel and the
 * command palette, so it survives Inertia navigation and any surface can open
 * it without threading props down.
 *
 * `?help=<slug>` opens it on load, which is what makes an assistant answer or a
 * support reply able to land somebody on the right explanation on the right
 * screen.
 */
export function HelpProvider({ children }: { children: ReactNode }) {
  const { help_slug: pageSlug, help_index: index } = usePage<SharedData>().props
  const [openSlug, setOpenSlug] = useState<string | null>(null)

  // Focus returns to whatever opened the sheet. Without this a keyboard user
  // who presses ESC is dropped at the top of the document and has to find
  // their place again.
  const trigger = useRef<HTMLElement | null>(null)

  const open = useCallback((slug: string) => {
    trigger.current = document.activeElement as HTMLElement | null
    setOpenSlug(slug)
  }, [])

  const close = useCallback(() => {
    setOpenSlug(null)

    // Drop `?help=` so the sheet does not reopen on back, and so a URL copied
    // after closing is the page rather than the page-plus-a-panel.
    const url = new URL(window.location.href)
    if (url.searchParams.has("help")) {
      url.searchParams.delete("help")
      window.history.replaceState(window.history.state, "", url.toString())
    }
  }, [])

  // Deep link, on first paint and on every Inertia visit that carries one.
  useEffect(() => {
    const fromUrl = () => {
      const slug = new URL(window.location.href).searchParams.get("help")
      if (slug) setOpenSlug(slug)
    }

    fromUrl()
    return router.on("navigate", fromUrl)
  }, [])

  // A sheet that survives a navigation is a sheet describing the wrong screen.
  useEffect(
    () =>
      router.on("before", () => {
        setOpenSlug(null)
      }),
    [],
  )

  const value = useMemo(
    () => ({ open, close, pageSlug: pageSlug ?? null, index: index ?? [], openSlug }),
    [open, close, pageSlug, index, openSlug],
  )

  return (
    <HelpContext.Provider value={value}>
      {children}
      <HelpSheet
        slug={openSlug}
        onClose={close}
        // Radix restores focus itself when the sheet closes, and without this
        // it lands on the body — a keyboard user who presses ESC is dropped at
        // the top of the document and has to find their place again. Taking
        // the restore over rather than racing it is the only version that wins.
        onCloseAutoFocus={(event) => {
          if (!trigger.current) return

          event.preventDefault()
          trigger.current.focus()
          trigger.current = null
        }}
      />
    </HelpContext.Provider>
  )
}
